import React, { useState, useEffect } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { GoogleLogin } from '@react-oauth/google'
import { useAuthStore, getDefaultRouteForUser } from '@/stores/authStore'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { useToast } from '@/hooks/use-toast'
import { Loader2 } from 'lucide-react'
import config from '@/config/config.js'
import AuthLoadingScreen from '@/components/AuthLoadingScreen'
import { AuthCard, AuthAside, AuthLabel, authLinkClass } from '@/components/auth/AuthCard'

const TOTAL_STEPS = 3

const toTitleCase = (str) =>
  str?.replace(/\w\S*/g, (w) => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase()) ?? ''

const LoginPage = () => {
  const [formData, setFormData] = useState({ email: '', password: '' })
  const [showPassword, setShowPassword] = useState(false)
  const [errors, setErrors] = useState({})
  const [isGoogleLoading, setIsGoogleLoading] = useState(false)

  const {
    login,
    googleLogin,
    fetchUserCompanies,
    selectCompany,
    resetAuthFlow,
    demoLogin,
    isLoading,
    isLoadingCompanies,
    isAuthenticating,
    companies,
    selectedCompany,
    authStep,
    error,
  } = useAuthStore()
  const { toast } = useToast()
  const navigate = useNavigate()

  useEffect(() => {
    resetAuthFlow()
  }, [])

  const handleChange = (e) => {
    const { name, value } = e.target
    setFormData((prev) => ({ ...prev, [name]: value }))
    if (errors[name]) setErrors((prev) => ({ ...prev, [name]: '' }))
  }

  const validateEmail = () => {
    const newErrors = {}
    if (!formData.email) newErrors.email = 'Email is required'
    else if (!config.validation.email.pattern.test(formData.email))
      newErrors.email = config.validation.email.message
    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const validatePassword = () => {
    const newErrors = {}
    if (!formData.password) newErrors.password = 'Password is required'
    else if (formData.password.length < config.validation.password.minLength)
      newErrors.password = config.validation.password.message
    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const handleEmailSubmit = async (e) => {
    e.preventDefault()
    if (!validateEmail()) return
    const result = await fetchUserCompanies(formData.email)
    if (!result.success) {
      toast({
        title: 'Error',
        description: result.error || 'Failed to fetch companies for this email.',
        variant: 'destructive',
      })
    }
  }

  const handleCompanySelect = (company) => selectCompany(company)

  const handlePasswordSubmit = async (e) => {
    e.preventDefault()
    if (!validatePassword()) return
    const result = await login(formData.email, formData.password)
    if (result.success) {
      toast({
        title: 'Welcome back!',
        description: "You've successfully signed in to HealthyCoach.",
        variant: 'success',
      })
      navigate(getDefaultRouteForUser(result.data?.userData))
    } else {
      toast({
        title: 'Sign in failed',
        description: result.error || 'Please check your credentials and try again.',
        variant: 'destructive',
      })
    }
  }

  const handleGoogleSuccess = async (credentialResponse) => {
    if (!credentialResponse?.credential) {
      toast({
        title: 'Google sign-in failed',
        description: 'No credential returned. Please try again.',
        variant: 'destructive',
      })
      return
    }
    try {
      setIsGoogleLoading(true)
      const result = await googleLogin(
        credentialResponse.credential,
        selectedCompany?.company?.id
      )
      if (result.success) {
        toast({ title: 'Welcome back!', description: 'Signed in with Google.', variant: 'success' })
        navigate(getDefaultRouteForUser(result.data?.userData))
      } else {
        toast({
          title: 'Google sign-in failed',
          description: result.error || 'Please try again.',
          variant: 'destructive',
        })
      }
    } finally {
      setIsGoogleLoading(false)
    }
  }

  const handleGoogleError = () => {
    toast({ title: 'Google sign-in failed', description: 'Please try again.', variant: 'destructive' })
  }

  const handleDemoLogin = async () => {
    const result = await demoLogin()
    if (result.success) {
      toast({
        title: 'Demo account loaded!',
        description: 'Welcome to the HealthyCoach demo.',
        variant: 'success',
      })
      navigate(getDefaultRouteForUser(result.data?.userData))
    } else {
      toast({
        title: 'Demo login failed',
        description: result.error || 'Failed to load demo account.',
        variant: 'destructive',
      })
    }
  }

  const handleBackToEmail = () => {
    resetAuthFlow()
    setFormData({ email: formData.email, password: '' })
    setErrors({})
  }

  if (isAuthenticating) return <AuthLoadingScreen />

  const inputError = (name) => (errors[name] ? 'border-red-300 focus-visible:border-red-500 focus-visible:ring-red-500/15' : '')

  const renderEmailStep = () => (
    <AuthCard step={1} totalSteps={TOTAL_STEPS} title="What's your email?" subtitle="We'll look up the workspaces you belong to.">
      <form onSubmit={handleEmailSubmit} noValidate>
        <label htmlFor="email" className="sr-only">Email address</label>
        <Input
          id="email"
          name="email"
          type="email"
          autoComplete="email"
          value={formData.email}
          onChange={handleChange}
          className={`h-12 ${inputError('email')}`}
          placeholder="you@company.com"
          disabled={isLoadingCompanies}
          aria-invalid={Boolean(errors.email)}
        />
        {errors.email && <p className="mt-1.5 text-xs text-red-600">{errors.email}</p>}
        {error && <p className="mt-1.5 text-xs text-red-600">{error}</p>}

        <div className="mt-2.5 text-right">
          <Link to="/auth/forgot-password" className={`text-sm ${authLinkClass}`}>
            Forgot password?
          </Link>
        </div>

        <Button type="submit" size="lg" className="mt-3 w-full" disabled={isLoadingCompanies}>
          {isLoadingCompanies ? (
            <>
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              Loading...
            </>
          ) : (
            'Continue'
          )}
        </Button>
      </form>

      <div className="my-4 flex items-center gap-3">
        <div className="h-px flex-1 bg-slate-100" />
        <span className="text-xs text-slate-500">or</span>
        <div className="h-px flex-1 bg-slate-100" />
      </div>

      <div className={`flex w-full justify-center ${isGoogleLoading ? 'pointer-events-none opacity-60' : ''}`}>
        <GoogleLogin
          onSuccess={handleGoogleSuccess}
          onError={handleGoogleError}
          useOneTap={false}
          text="continue_with"
          shape="rectangular"
          size="large"
          theme="outline"
          width={388}
        />
      </div>
    </AuthCard>
  )

  const renderCompanyStep = () => (
    <AuthCard step={2} totalSteps={TOTAL_STEPS} title="Select your workspace" subtitle={`Signing in as ${formData.email}.`}>
      <div className="space-y-2">
        {companies.map((companyData) => (
          <button
            key={companyData.id}
            type="button"
            onClick={() => handleCompanySelect(companyData)}
            className="flex w-full items-center gap-3 rounded-[10px] border border-slate-200 bg-white p-3 text-left transition-colors hover:bg-slate-50"
          >
            <div className="flex h-[34px] w-[34px] flex-shrink-0 items-center justify-center rounded-lg bg-accent text-sm font-semibold text-primary">
              {companyData.company.name?.charAt(0)?.toUpperCase() ?? '?'}
            </div>
            <div className="min-w-0 flex-1">
              <div className="truncate text-[15px] font-semibold text-slate-900">{companyData.company.name}</div>
              {typeof companyData.role === 'string' && companyData.role && (
                <div className="truncate text-xs text-slate-500">{companyData.role}</div>
              )}
            </div>
          </button>
        ))}
      </div>

      <button
        type="button"
        onClick={handleBackToEmail}
        className="mt-4 text-sm text-slate-500 transition-colors hover:text-slate-900"
      >
        <span aria-hidden="true">← </span>Use a different email
      </button>
    </AuthCard>
  )

  const renderPasswordStep = () => (
    <AuthCard step={3} totalSteps={TOTAL_STEPS} title="Enter your password">
      <div className="mb-4 flex items-center gap-3 rounded-[10px] border border-slate-200 bg-background p-3">
        <div className="flex h-[30px] w-[30px] flex-shrink-0 items-center justify-center rounded-lg bg-accent text-[13px] font-semibold text-primary">
          {selectedCompany?.company?.name?.charAt(0)?.toUpperCase() ?? '?'}
        </div>
        <div className="min-w-0 flex-1">
          <div className="truncate text-sm font-semibold text-slate-900">{toTitleCase(selectedCompany?.company?.name)}</div>
          <div className="truncate text-xs text-slate-500">{formData.email}</div>
        </div>
        <button type="button" onClick={handleBackToEmail} className={`text-[13px] ${authLinkClass}`}>
          Change
        </button>
      </div>

      <form onSubmit={handlePasswordSubmit} noValidate>
        <AuthLabel htmlFor="password">Password</AuthLabel>
        <div className="relative">
          <Input
            id="password"
            name="password"
            type={showPassword ? 'text' : 'password'}
            autoComplete="current-password"
            value={formData.password}
            onChange={handleChange}
            className={`h-12 pr-16 ${inputError('password')}`}
            placeholder="Enter your password"
            disabled={isLoading}
            aria-invalid={Boolean(errors.password)}
            autoFocus
          />
          <button
            type="button"
            onClick={() => setShowPassword(!showPassword)}
            aria-label={showPassword ? 'Hide password' : 'Show password'}
            className="absolute right-3.5 top-1/2 -translate-y-1/2 text-[13px] text-slate-500 transition-colors hover:text-slate-900"
          >
            {showPassword ? 'Hide' : 'Show'}
          </button>
        </div>
        {errors.password && <p className="mt-1.5 text-xs text-red-600">{errors.password}</p>}
        {error && <p className="mt-1.5 text-xs text-red-600">{error}</p>}

        <div className="my-3.5 flex items-center justify-between">
          <label className="flex cursor-pointer select-none items-center gap-2">
            <input type="checkbox" className="h-4 w-4 rounded border-slate-300 text-primary focus:ring-primary" />
            <span className="text-sm text-slate-500">Remember me</span>
          </label>
          <Link to="/auth/forgot-password" className={`text-sm ${authLinkClass}`}>
            Forgot password?
          </Link>
        </div>

        <Button type="submit" size="lg" className="w-full" disabled={isLoading}>
          {isLoading ? (
            <>
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              Signing in...
            </>
          ) : (
            'Sign in'
          )}
        </Button>
      </form>
    </AuthCard>
  )

  return (
    <div>
      {authStep === 'email' && renderEmailStep()}
      {authStep === 'company' && renderCompanyStep()}
      {authStep === 'password' && renderPasswordStep()}

      <AuthAside>
        No account yet?{' '}
        <Link to="/auth/signup" className={authLinkClass}>
          Sign up
        </Link>
      </AuthAside>
    </div>
  )
}

export default LoginPage
